import {
  getFinancingRepaymentDueDate,
  isFinancingApplicationLate,
  type FinancingApplication,
  type FinancingOffer,
} from '@hero/schema';
import {
  TERM_DURATION_OPTIONS,
  type ApplicationMock,
  type OfferMock,
  type OfferTermsMock,
  type TermDurationMock,
} from '../mocks/types';

/**
 * Adapter — convert a real `FinancingApplication` from the API into
 * the `ApplicationMock` shape the Phase A UI components already
 * understand. Keeping the adapter narrow lets us reuse every visual
 * component (StatusBadge, ApplicationsTable, ApplicationRowDetails)
 * without a parallel "real-data" branch of components.
 *
 * Phase B-extras can collapse the mock types into the schema types
 * directly; this adapter is the bridge until then.
 */

/**
 * Map the schema's `TermDuration` (`{ value, type, dueDate? }`) onto
 * the mock UI's 5-option enum (`30 days` / `60 days` / `90 days` /
 * `6 months` / `1 year`). Falls back to `30 days` when the term
 * doesn't match — should never happen with the seeded options but
 * is a safe default.
 */
function termToMock(term: {
  value: number;
  type: 'immediate' | 'day' | 'week' | 'month' | 'manual';
}): TermDurationMock {
  if (term.type === 'day') {
    if (term.value === 30) return TERM_DURATION_OPTIONS[0];
    if (term.value === 60) return TERM_DURATION_OPTIONS[1];
    if (term.value === 90) return TERM_DURATION_OPTIONS[2];
  }
  if (term.type === 'month') {
    if (term.value === 6) return TERM_DURATION_OPTIONS[3];
    if (term.value === 12) return TERM_DURATION_OPTIONS[4];
  }
  return TERM_DURATION_OPTIONS[0];
}

function lenderIdFromOffer(offer: FinancingOffer): string {
  const embedded = offer.lender?._id;
  if (embedded != null) {
    return typeof embedded === 'string' ? embedded : embedded.toHexString();
  }
  // Legacy rows may still carry a top-level `lenderId` before the
  // MVP rename migration embeds `{ lender: { _id, name } }`.
  const legacy = (offer as FinancingOffer & { lenderId?: string | null }).lenderId;
  return legacy ? String(legacy) : '';
}

function lenderNameFromOffer(offer: FinancingOffer): string {
  if (offer.lender?.name) return offer.lender.name;
  const legacy = (offer as FinancingOffer & { lenderName?: string }).lenderName;
  return legacy ?? 'Lender';
}

function offerToMock(offer: FinancingOffer): OfferMock {
  let terms: OfferTermsMock | undefined;
  if (offer.terms) {
    terms = {
      advancedAmount: offer.terms.advancedAmount,
      feeAmount: offer.terms.feeAmount,
      feePercentage: offer.terms.feePercentage,
      term: termToMock(offer.terms.term),
      repaymentDate: offer.terms.repaymentDate
        ? new Date(offer.terms.repaymentDate).toISOString()
        : undefined,
      recourse: offer.terms.recourse,
      notes: offer.terms.notes,
    };
  }
  return {
    id: offer.id,
    lenderId: lenderIdFromOffer(offer),
    lenderName: lenderNameFromOffer(offer),
    decision: offer.decision,
    decidedAt: offer.decidedAt
      ? new Date(offer.decidedAt).toISOString()
      : undefined,
    terms,
    isCounterOffer: offer.isCounterOffer,
    declineReason: offer.declineReason,
    declineNotes: offer.declineNotes,
  };
}

export function applicationToMock(app: FinancingApplication): ApplicationMock {
  return {
    id: app._id.toString(),
    ref: app.ref?.main ?? app._id.toString(),
    invoice: {
      source: app.invoice.source,
      id: app.invoice._id?.toString(),
      clientName: app.invoice.clientName,
      amount: app.invoice.amount.total,
      currency: 'SAR',
      expectedDueDate: app.invoice.expectedDueDate
        ? new Date(app.invoice.expectedDueDate).toISOString()
        : new Date().toISOString(),
      invoiceNumber: app.invoice.invoiceNumber,
      documentUrl: app.invoice.document?.url,
    },
    requestedAmount: app.requestedAmount,
    requestedTerm: termToMock(app.requestedTerm),
    profileSnapshot: {
      fleetTier: app.profileSnapshot.fleetTier,
      annualRevenueTier: app.profileSnapshot.annualRevenueTier,
      financingNeeds: app.profileSnapshot.financingNeeds,
      ownsAllTrucks: app.profileSnapshot.ownsAllTrucks,
      trucksOwnedCount: app.profileSnapshot.trucksOwnedCount,
      hasLoans: app.profileSnapshot.hasLoans,
      monthlyFinancingPayment: app.profileSnapshot.monthlyFinancingPayment,
      isProfitable: app.profileSnapshot.isProfitable,
      financialStatements: app.profileSnapshot.financialStatements?.map(
        statement => ({ id: statement.id, url: statement.url }),
      ),
    },
    status: app.status,
    offers: (app.offers ?? []).map(offerToMock),
    tags: app.tags,
    isLate: isFinancingApplicationLate(app),
    dueDate: getFinancingRepaymentDueDate(app)?.toISOString(),
    repaidAt: app.outcome?.repaidAt
      ? new Date(app.outcome.repaidAt).toISOString()
      : undefined,
    /**
     * Carry the last rejection reason ONLY while the application
     * is back in `funded` AND a fresh receipt hasn't landed —
     * mirrors the projection in `buildLenderView`. The moment the
     * carrier re-submits, `repaymentReceipt` becomes set and the
     * banner stops rendering on the carrier side.
     */
    lastRejectionNotes:
      app.status === 'funded' && !app.repaymentReceipt
        ? app.outcome?.notes
        : undefined,
    createdAt: app.doc?.createdAt
      ? new Date(app.doc.createdAt).toISOString()
      : new Date().toISOString(),
    updatedAt: app.doc?.updatedAt
      ? new Date(app.doc.updatedAt).toISOString()
      : new Date().toISOString(),
  };
}

/**
 * Reverse direction — convert the mock `TermDurationMock` to the
 * schema `TermDuration` shape. Used by the apply-form submit path
 * when we POST to `useFinancingCreateApplicationApi`.
 */
export function termFromMock(term: TermDurationMock): {
  value: number;
  type: 'day' | 'month';
} {
  if (term.unit === 'days') return { value: term.value, type: 'day' };
  if (term.unit === 'months') return { value: term.value, type: 'month' };
  // 1 year — express as 12 months for consistency with the seeded options.
  return { value: 12, type: 'month' };
}
